import { useReducer, useEffect, useRef } from 'react';
import axios from '@common/axios';

const initialState = {
    isSubmitting: false,
    answers: {} // { [questionId]: { isChecked, isCorrect, userAnswers, backendFeedback, previousAnswerIncorrect, currentInputs, savedQuestionInputs } }
};

function answerReducer(state, action) {
    switch (action.type) {
        case 'SET_SUBMITTING':
            return { ...state, isSubmitting: action.payload };

        case 'CAPTURE_INPUTS':
            return {
                ...state,
                answers: {
                    ...state.answers,
                    [action.payload.questionId]: {
                        ...state.answers[action.payload.questionId],
                        currentInputs: action.payload.currentInputs,
                        savedQuestionInputs: action.payload.savedQuestionInputs
                    }
                }
            };

        case 'CHECK_ANSWER_SUCCESS':
            return {
                ...state,
                answers: {
                    ...state.answers,
                    [action.payload.questionId]: {
                        ...state.answers[action.payload.questionId],
                        isChecked: true,
                        isCorrect: action.payload.isCorrect,
                        userAnswers: action.payload.userAnswers,
                        backendFeedback: action.payload.backendFeedback,
                        previousAnswerIncorrect: !action.payload.isCorrect
                    }
                }
            };

        case 'RETRY_QUESTION':
            const nextAnswers = { ...state.answers };
            if (nextAnswers[action.payload]) {
                nextAnswers[action.payload] = {
                    ...nextAnswers[action.payload],
                    isChecked: false,
                    isCorrect: undefined,
                    userAnswers: [],
                    backendFeedback: undefined,
                    // We intentionally leave savedQuestionInputs empty to force a clean slate
                    savedQuestionInputs: undefined,
                    currentInputs: []
                };
            }
            return { ...state, answers: nextAnswers };

        case 'RESET_ALL':
            return initialState;

        default:
            return state;
    }
}

const useQuestionAnswering = (conceptId, selectedLevel, selectedQuestionId, questionList) => {
    const [state, dispatch] = useReducer(answerReducer, initialState);
    const questionContentRef = useRef({});
    const stateRef = useRef(state);

    // Keep an updated ref for DOM synchronization callbacks
    useEffect(() => {
        stateRef.current = state;
    }, [state]);

    // Filter out duplicate question IDs from the backend to ensure accurate stats
    const uniqueQuestionList = Array.from(
        new Map((questionList || []).map(q => [q.questionId, q])).values()
    );

    // Lock interactive elements when a question is checked
    useEffect(() => {
        if (selectedQuestionId && questionContentRef.current[selectedQuestionId]) {
            const container = questionContentRef.current[selectedQuestionId];
            const isChecked = !!state.answers[selectedQuestionId]?.isChecked;
            container.querySelectorAll('input, select, textarea').forEach(el => {
                el.disabled = isChecked;
                el.style.opacity = isChecked ? '0.6' : '1';
                el.style.cursor = isChecked ? 'not-allowed' : 'auto';
            });
        }
    }, [selectedQuestionId, state.answers]);

    // Restore inputs when returning to a previously viewed question
    useEffect(() => {
        if (selectedQuestionId) restoreQuestionInputs(selectedQuestionId);
    }, [selectedQuestionId]); // eslint-disable-line react-hooks/exhaustive-deps

    const getUserSelectedAnswers = (questionId) => {
        const container = questionContentRef.current[questionId];
        if (!container) return [];
        const selectedAnswers = [];
        container.querySelectorAll('input[type="checkbox"]:checked').forEach(cb => {
            const match = (cb.value || '').match(/checkbox-([a-z])/i);
            if (match) selectedAnswers.push(match[1].toLowerCase());
        });
        container.querySelectorAll('input[type="radio"]:checked').forEach(radio => {
            const match = (radio.value || '').match(/radio-([a-z])/i);
            if (match) selectedAnswers.push(match[1].toLowerCase());
        });
        container.querySelectorAll('input[type="text"], input[type="number"], input:not([type])').forEach(input => {
            if (input.value?.trim()) selectedAnswers.push(input.value.trim());
        });
        container.querySelectorAll('select').forEach(select => {
            if (select.value && select.value !== '') selectedAnswers.push(select.value);
        });
        return selectedAnswers;
    };

    const captureQuestionInputs = (questionId) => {
        const container = questionContentRef.current[questionId];
        if (!container) return;
        const snapshot = Array.from(container.querySelectorAll('input, select, textarea')).map(el => ({
            tagName: el.tagName, type: el.type, name: el.name, value: el.value,
            checked: !!el.checked, selectedIndex: el.tagName === 'SELECT' ? el.selectedIndex : undefined
        }));

        dispatch({
            type: 'CAPTURE_INPUTS',
            payload: {
                questionId,
                currentInputs: getUserSelectedAnswers(questionId),
                savedQuestionInputs: snapshot
            }
        });
    };

    const restoreQuestionInputs = (questionId, providedContainer = null) => {
        const container = providedContainer || questionContentRef.current[questionId];
        const answerData = stateRef.current.answers[questionId] || {};
        const snapshot = answerData.savedQuestionInputs;

        if (!container || !snapshot) return;

        const isChecked = !!answerData.isChecked;
        Array.from(container.querySelectorAll('input, select, textarea')).forEach((el, index) => {
            const saved = snapshot[index];
            if (!saved) return;
            if (el.type === 'checkbox' || el.type === 'radio') {
                el.checked = !!saved.checked;
            } else if (el.tagName === 'SELECT') {
                if (saved.value !== undefined && saved.value !== null) el.value = saved.value;
                else if (saved.selectedIndex !== undefined) el.selectedIndex = saved.selectedIndex;
            } else {
                el.value = saved.value ?? '';
            }
            if (isChecked) {
                el.disabled = true; el.style.opacity = '0.6'; el.style.cursor = 'not-allowed';
            }
        });
    };

    const captureCurrentQuestion = (questionId) => {
        if (!questionId) return;
        captureQuestionInputs(questionId);
    };

    const handleInputChange = (questionId) => captureCurrentQuestion(questionId);

    const handleCheckAnswer = async (questionId) => {
        const container = questionContentRef.current[questionId];
        if (!container) return false;

        const formData = new URLSearchParams();
        container.querySelectorAll('input, select, textarea').forEach(el => {
            if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) return;
            if (el.name && el.value !== undefined) formData.append(el.name, el.value);
        });

        formData.append('conceptId', conceptId);
        formData.append('questionId', questionId);
        formData.append('level', selectedLevel.value);

        const previousAnswerIncorrect = state.answers[questionId]?.previousAnswerIncorrect;
        formData.append('previousAnswerIncorrect', previousAnswerIncorrect ? 'true' : 'false');

        try {
            dispatch({ type: 'SET_SUBMITTING', payload: true });
            const response = await axios.post('/web/ui/question/answer', formData);
            if (response.data.hasError && response.data.result === undefined) throw new Error();

            dispatch({
                type: 'CHECK_ANSWER_SUCCESS',
                payload: {
                    questionId,
                    isCorrect: response.data.result,
                    userAnswers: getUserSelectedAnswers(questionId),
                    backendFeedback: {
                        correctAnswer: response.data.correctAnswer,
                        correctAnswerFeedback: response.data.correctAnswerFeedback
                    }
                }
            });
            return true;
        } catch {
            alert('There was an error communicating with the server. Please try again.');
            return false;
        } finally {
            dispatch({ type: 'SET_SUBMITTING', payload: false });
        }
    };

    const handleRetry = (questionId) => {
        dispatch({ type: 'RETRY_QUESTION', payload: questionId });

        const container = questionContentRef.current[questionId];
        if (!container) return;
        container.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach(cb => cb.checked = false);
        container.querySelectorAll('input[type="text"], input[type="number"], input:not([type])').forEach(input => input.value = '');
        container.querySelectorAll('select').forEach(select => select.selectedIndex = 0);
    };

    const getProgressStats = () => {
        // Use the uniquely filtered list so duplicates from the backend don't mess up our total
        const total = uniqueQuestionList.length || 0;
        const answeredIds = Object.keys(state.answers).filter(id => state.answers[id].isChecked);
        const answered = answeredIds.length;
        const correctCount = answeredIds.filter(id => state.answers[id].isCorrect).length;

        return {
            total, answered, correctCount,
            percentage: total > 0 ? Math.round((answered / total) * 100) : 0,
            accuracy: answered > 0 ? Math.round((correctCount / answered) * 100) : 0
        };
    };

    const formatCorrectAnswer = (questionId, fallbackAnswer, answerType) => {
        const backendAns = state.answers[questionId]?.backendFeedback?.correctAnswer;
        if (backendAns) return backendAns;
        if (!fallbackAnswer) return 'N/A';
        if (answerType === 'TEXTFIELD') return fallbackAnswer;
        return answerType === 'CHECKBOX'
            ? fallbackAnswer.split('|').map(a => a.toUpperCase()).join(' and ')
            : fallbackAnswer.split('|')[0].toUpperCase();
    };

    const formatUserAnswer = (userAnswerArray, answerType) => {
        if (!userAnswerArray || userAnswerArray.length === 0) return 'None entered';
        return answerType === 'TEXTFIELD'
            ? userAnswerArray[0] || 'None entered'
            : userAnswerArray.map(a => a.toUpperCase()).join(' and ');
    };

    const resetAnswers = () => dispatch({ type: 'RESET_ALL' });

    // Reconstruct the flat variables to keep backwards compatibility with `Questions.js`
    const checkedQuestions = {};
    const isCorrect = {};
    const userAnswers = {};
    const currentInputs = {};

    Object.keys(state.answers).forEach(qId => {
        if (state.answers[qId].isChecked) checkedQuestions[qId] = true;
        if (state.answers[qId].isCorrect !== undefined) isCorrect[qId] = state.answers[qId].isCorrect;
        if (state.answers[qId].userAnswers) userAnswers[qId] = state.answers[qId].userAnswers;
        if (state.answers[qId].currentInputs) currentInputs[qId] = state.answers[qId].currentInputs;
    });

    return {
        state, // Exposed for future grouping
        isSubmitting: state.isSubmitting,
        checkedQuestions,
        isCorrect,
        userAnswers,
        currentInputs,
        questionContentRef,
        handleInputChange,
        handleCheckAnswer,
        handleRetry,
        captureCurrentQuestion,
        restoreQuestionInputs,
        getProgressStats,
        formatCorrectAnswer,
        formatUserAnswer,
        resetAnswers
    };
};

export default useQuestionAnswering;